/**
 * Token image mapping for cross-chain tokens
 * Maps tokens on other chains to their Ethereum equivalents for image display
 * Format: networkType:chainId:tokenAddress -> { chainId: ethereumChainId, address: ethereumAddress }
 */

import { zeroAddress } from 'viem'
import { IMAGE_SERVER_URL } from '../config/app'
import { NetworkTypes } from '@provex/eip712-utils'

interface TokenImageMapping {
  chainId: number
  address: string
  symbolOverride?: string
  nameOverride?: string
}

const TOKEN_IMAGE_MAPPINGS: Record<string, TokenImageMapping> = {
  // BSC (56) tokens mapped to Ethereum equivalents
  [`${NetworkTypes.EVM}:56:0x0555E30da8f98308EdB960aa94C0Db47230d2B9c`.toLowerCase()]: {
    chainId: 1, // Ethereum
    address: '0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599', // WBTC on Ethereum
  },
  // PulseChain (369) common tokens mapped to Ethereum equivalents
  [`${NetworkTypes.EVM}:369:0x02dcdd04e3f455d838cd1249292c58f3b79e3c3c`.toLowerCase()]: {
    chainId: 1, // WETH on Ethereum
    address: '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2',
  },
  [`${NetworkTypes.EVM}:369:0xefd766ccb38eaf1dfd701853bfce31359239f305`.toLowerCase()]: {
    chainId: 1, // DAI on Ethereum
    address: '0x6B175474E89094C44Da98b954EedeAC495271d0F',
  },
  // Arbitrum (42161) tokens mapped to canonical images
  [`${NetworkTypes.EVM}:42161:0x1b896893dfc86bb67cf57767298b9073d2c1ba2c`.toLowerCase()]: {
    chainId: 56, // CAKE on BSC
    address: '0x0E09FAbb73BD3Ade0a17ECC321fD13a19e81cE82',
  },
  [`${NetworkTypes.EVM}:42161:0x2f2a2543b76a4166549f7aab2e75bef0aefc5b0f`.toLowerCase()]: {
    chainId: 1, // WBTC on Ethereum
    address: '0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599',
  },
  // Solana (501) common tokens mapped to Ethereum equivalents for consistent icons
  // USDC (Solana) -> USDC (Ethereum)
  [`${NetworkTypes.SOLANA}:501:EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v`.toLowerCase()]: {
    chainId: 1,
    address: '0xA0b86991c6218B36C1d19D4a2e9Eb0Ce3606eB48',
    symbolOverride: 'USDC',
    nameOverride: 'USD Coin',
  },
  // WIF (Solana dogwifhat) – symbol/name override
  [`${NetworkTypes.SOLANA}:501:EKpQGSJtjMFqKZ9KQanSqYXRcF8fBopzLHYxdM65zcjm`.toLowerCase()]: {
    chainId: 1,
    address: '0x0000000000000000000000000000000000000000',
    symbolOverride: 'WIF',
    nameOverride: 'dogwifhat',
  },
  // wSOL mint – symbol/name override to SOL
  [`${NetworkTypes.SOLANA}:501:So11111111111111111111111111111111111111112`.toLowerCase()]: {
    chainId: 1,
    address: '0x0000000000000000000000000000000000000000',
    symbolOverride: 'SOL',
    nameOverride: 'Solana',
  },
  [`${NetworkTypes.EVM}:56:0x8965349fb649A33a30cbFDa057D8eC2C48AbE2A2`.toLowerCase()]: {
    chainId: 1,
    address: '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48',
  },
  [`${NetworkTypes.EVM}:56:0x3E14602186DD9dE538F729547B3918D24c823546`.toLowerCase()]: {
    chainId: 56,
    address: '0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c',
  },
  [`${NetworkTypes.EVM}:1:0x418708dD507A2F0Cac24d31c60B350315F4C8009`.toLowerCase()]: {
    chainId: 8453,
    address: '0xc27468b12ffA6d714B1b5fBC87eF403F38b82AD4',
  },
  [`${NetworkTypes.EVM}:56:0x7950865a9140cb519342433146ed5b40c6f210f7`.toLowerCase()]: {
    chainId: 1,
    address: '0x45804880de22913dafe09f4980848ece6ecbaf78',
  },
  [`${NetworkTypes.EVM}:56:0x9996D0276612d23b35f90C51EE935520B3d7355B`.toLowerCase()]: {
    chainId: 1,
    address: '0xd533a949740bb3306d119cc777fa900ba034cd52',
  },
  [`${NetworkTypes.EVM}:56:0x58538e6A46E07434d7E7375Bc268D3cb839C0133`.toLowerCase()]: {
    chainId: 1,
    address: '0x57e114b691db790c35207b2e685d4a43181e6061',
  },
  [`${NetworkTypes.EVM}:1:0x4fabb145d64652a948d72533023f6e7a623c7c53`.toLowerCase()]: {
    chainId: 56,
    address: '0xe9e7cea3dedca5984780bafc599bd69add087d56',
  },
  [`${NetworkTypes.EVM}:137:0x06d02e9d62a13fc76bb229373fb3bbbd1101d2fc`.toLowerCase()]: {
    chainId: 1,
    address: '0x2af5d2ad76741191d15dfe7bf6ac92d4bd912ca3',
  },
  [`${NetworkTypes.EVM}:137:0x67ce67ec4fcd4aca0fcb738dd080b2a21ff69d75`.toLowerCase()]: {
    chainId: 1,
    address: '0xba9d4199fab4f26efe3551d490e3821486f135ba',
  },
  [`${NetworkTypes.EVM}:43114:0x43eDD7f3831b08FE70B7555ddD373C8bF65a9050`.toLowerCase()]: {
    chainId: 1,
    address: '0x5e8422345238f34275888049021821e8e08caa1f',
  },
  [`${NetworkTypes.EVM}:43114:0x2018B0CA0eDcE80800851958bD094dD4a8DA1fc4`.toLowerCase()]: {
    chainId: 1,
    address: '0x5e8422345238f34275888049021821e8e08caa1f',
  },
  [`${NetworkTypes.EVM}:43114:0x3cD790449CF7D187a143d4Bd7F4654d4f2403e02`.toLowerCase()]: {
    chainId: 1,
    address: '0x42bbfa2e77757c645eeaad1655e0911a7553efbc',
  },
  [`${NetworkTypes.EVM}:43114:0xe80772eaf6e2e18b651f160bc9158b2a5cafca65`.toLowerCase()]: {
    chainId: 137,
    address: '0x236eec6359fb44cce8f97e99387aa7f8cd5cde1f',
  },
  [`${NetworkTypes.EVM}:43114:0xeebc562d445f4bc13ac75c8caabb438dfae42a1b`.toLowerCase()]: {
    chainId: 1,
    address: '0xd533a949740bb3306d119cc777fa900ba034cd52',
  },
  [`${NetworkTypes.EVM}:8453:0x7eb8d1e4e2d0c8b9beda7a97b305cf49f3eee8da`.toLowerCase()]: {
    chainId: 1,
    address: '0x5e8422345238f34275888049021821e8e08caa1f',
  },
  [`${NetworkTypes.EVM}:8453:0xf3527ef8de265eaa3716fb312c12847bfba66cef`.toLowerCase()]: {
    chainId: 56,
    address: '0xf3527ef8de265eaa3716fb312c12847bfba66cef',
  },
  [`${NetworkTypes.EVM}:8453:0x959fc04dbf97a27073f89237cd62605f4d1b906d`.toLowerCase()]: {
    chainId: 137,
    address: '0x7b12598e3616261df1c05ec28de0d2fb10c1f206',
  },
  [`${NetworkTypes.EVM}:8453:0xF469fBD2abcd6B9de8E169d128226C0Fc90a012e`.toLowerCase()]: {
    chainId: 56,
    address: '0xf9c4ff105803a77ecb5dae300871ad76c2794fa4',
  },
  [`${NetworkTypes.EVM}:8453:0x1A6B3A62391ECcaaa992ade44cd4AFe6bEC8CfF1`.toLowerCase()]: {
    chainId: 42161,
    address: '0x1a6b3a62391eccaaa992ade44cd4afe6bec8cff1',
  },
  [`${NetworkTypes.EVM}:8453:${zeroAddress}`.toLowerCase()]: {
    chainId: 1,
    address: zeroAddress,
  },
  // Monad (chain ID 143) - native token uses chain ID for image
  [`${NetworkTypes.EVM}:143:0x0000000000000000000000000000000000000000`.toLowerCase()]: {
    chainId: 143,
    address: '0x0000000000000000000000000000000000000000',
    symbolOverride: 'MONAD',
    nameOverride: 'Monad',
  },
  // MegaETH (chain ID 4326) - native token uses chain ID for image
  [`${NetworkTypes.EVM}:4326:0x0000000000000000000000000000000000000000`.toLowerCase()]: {
    chainId: 4326,
    address: '0x0000000000000000000000000000000000000000',
    symbolOverride: 'MEGAETH',
    nameOverride: 'MegaETH',
  },
  // Ethereum (chain ID 1) - native token ETH
  [`${NetworkTypes.EVM}:1:0x0000000000000000000000000000000000000000`.toLowerCase()]: {
    chainId: 1,
    address: '0x0000000000000000000000000000000000000000',
    symbolOverride: 'ETH',
    nameOverride: 'Ethereum',
  },
  [`${NetworkTypes.EVM}:480:0x0000000000000000000000000000000000000000`.toLowerCase()]: {
    chainId: 1,
    address: '0x0000000000000000000000000000000000000000',
    symbolOverride: 'ETH',
    nameOverride: 'Ether',
  },
  [`${NetworkTypes.EVM}:2741:0x0000000000000000000000000000000000000000`.toLowerCase()]: {
    chainId: 1,
    address: '0x0000000000000000000000000000000000000000',
    symbolOverride: 'ETH',
    nameOverride: 'Ether',
  },
  // Add more mappings as needed
  // Format: 'networkType:chainId:tokenAddress' -> { chainId: targetChainId, address: targetAddress }
}

/**
 * Get the mapped token for image display
 * @param networkType - The network type of the token
 * @param chainId - The chain ID of the token
 * @param tokenAddress - The address of the token
 * @returns The mapped token info or null if no mapping exists
 */
export const getMappedTokenImage = (
  networkType: string,
  chainId: number,
  tokenAddress: string
): TokenImageMapping | null => {
  const fullKey = `${networkType}:${chainId}:${tokenAddress.toLowerCase()}`
  return TOKEN_IMAGE_MAPPINGS[fullKey] || null
}

/**
 * Get chain image URL, with override for Monad (chain ID 143)
 * @param chainId - The chain ID
 * @returns The image URL for the chain
 */
const getChainImageUrl = (chainId: number): string => {
  // Override Monad (chain ID 143) to use local image
  if (chainId === 143) {
    return '/monad_logo.png'
  }
  if (chainId === 4326) {
    return '/megaeth_logo.svg'
  }
  return `${IMAGE_SERVER_URL}/image/${chainId}`
}

/**
 * Get the token image URL, using mapping if available
 * @param networkType - The network type of the token
 * @param chainId - The chain ID of the token
 * @param tokenAddress - The address of the token
 * @returns The image URL for the token
 */
export const getTokenImageUrl = (
  networkType: string,
  chainId: number,
  tokenAddress: string
): string => {
  // Check if there's a mapping for this token first (before default behavior)
  const mapped = getMappedTokenImage(networkType, chainId, tokenAddress)

  if (mapped) {
    // For mapped native tokens, use chain icon if address is zero
    if (mapped.address === zeroAddress) {
      return getChainImageUrl(mapped.chainId)
    }
    // Use the mapped token's image
    return `${IMAGE_SERVER_URL}/image/${mapped.chainId}/${mapped.address}`
  }

  // For EVM native tokens (zero address) without mapping, use chain icon
  if (networkType === NetworkTypes.EVM && tokenAddress === zeroAddress) {
    return getChainImageUrl(chainId)
  }

  // Use the original token's image
  return `${IMAGE_SERVER_URL}/image/${chainId}/${tokenAddress}`
}
